import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllPaddleProducts,
  getPaddleProduct,
  createPaddleProduct,
  updatePaddleProduct,
  addPriceToProduct,
  getProductPrices,
  getFeatureGroups,
  getProductEditions,
  archivePaddleProduct,
} from "./thunks";
import { IPaddleProductState, IPaddleProduct } from "./types";

const initialState: IPaddleProductState = {
  productList: [],
  currentProduct: null,
  isLoading: false,
  featureGroups: [],
  productEditions: [],
};

export const paddleProductSlice = createSlice({
  name: "paddleProducts",
  initialState,
  reducers: {
    setCurrentProduct: (state, action: PayloadAction<IPaddleProduct | null>) => {
      state.currentProduct = action.payload;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    // Get All Products
    builder
      .addCase(getAllPaddleProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllPaddleProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productList = action.payload;
      })
      .addCase(getAllPaddleProducts.rejected, (state) => {
        state.isLoading = false;
      });

    // Get Single Product
    builder
      .addCase(getPaddleProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPaddleProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload;
      })
      .addCase(getPaddleProduct.rejected, (state) => {
        state.isLoading = false;
      });

    // Create Product
    builder
      .addCase(createPaddleProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPaddleProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productList.push(action.payload);
        state.currentProduct = action.payload;
      })
      .addCase(createPaddleProduct.rejected, (state) => {
        state.isLoading = false;
      });

    // Update Product
    builder
      .addCase(updatePaddleProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePaddleProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.productList.findIndex(
          (p) => p.productId === action.payload.productId
        );
        if (index !== -1) {
          state.productList[index] = { ...state.productList[index], ...action.payload };
        }
        if (state.currentProduct?.productId === action.payload.productId) {
          state.currentProduct = { ...state.currentProduct, ...action.payload };
        }
      })
      .addCase(updatePaddleProduct.rejected, (state) => {
        state.isLoading = false;
      });

    // Add Price
    builder
      .addCase(addPriceToProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPriceToProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        const product = state.productList.find(
          (p) => p.productId === action.payload.productId
        );
        if (product) {
          product.prices.push(action.payload);
        }
        if (state.currentProduct?.productId === action.payload.productId) {
          state.currentProduct.prices.push(action.payload);
        }
      })
      .addCase(addPriceToProduct.rejected, (state) => {
        state.isLoading = false;
      });

    // Get Prices
    builder.addCase(getProductPrices.fulfilled, (state, action) => {
      const product = state.productList.find(
        (p) => p.productId === action.payload.productId
      );
      if (product) {
        product.prices = action.payload.prices;
      }
      if (state.currentProduct?.productId === action.payload.productId) {
        state.currentProduct.prices = action.payload.prices;
      }
    });

    // Get Feature Groups
    builder.addCase(getFeatureGroups.fulfilled, (state, action) => {
      state.featureGroups = action.payload;
    });

    // Get Product Editions
    builder.addCase(getProductEditions.fulfilled, (state, action) => {
      state.productEditions = action.payload;
    });

    // Archive Product
    builder
      .addCase(archivePaddleProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(archivePaddleProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove archived product from the list
        state.productList = state.productList.filter(
          (p) => p.productId !== action.payload.productId
        );
        if (state.currentProduct?.productId === action.payload.productId) {
          state.currentProduct = null;
        }
      })
      .addCase(archivePaddleProduct.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setCurrentProduct, clearCurrentProduct } = paddleProductSlice.actions;
export default paddleProductSlice.reducer;

